import { CellOfRow } from '@components/tables';
import { StreamType } from '@EcamModel/model/PTZRecordSetting';
import { IntervalUnit, NotifyItem, StorageFilenameType, UploadMethod } from '@EcamModel/model/PTZSnapshotSetting';
import {
    NasMountType,
    NasRecycle,
    NasStatus,
    SdCardDiskStatus,
    SdCardFullStatus,
    SdCardStatus,
    SdEncryptStatus,
} from '@EcamModel/model/PTZStorageSetting';
import { Button, Stack, TableBody, TableRow, Typography, IconButton } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import color from '@theme/Colors';
import { useEffect, useState } from 'react';
import { BiHide, BiShow } from 'react-icons/bi';
import { transformScheduleData } from 'src/helpers';
import { BaseSectionCollapse, SectionHasSubItemHeader, SectionHasSubItemProps } from './BasicInfoMilesightCameraPTZ';
import useMileSightPTZ, { SectionsCollapsePTZ } from '@pages/cameras/hook/useMilesightPTZ';
import PopUpEditSnapshotSettings, { PTZSnapShotSettingFormValues } from './PopUpEditSnapshotSettings';
import IcEdit from '@assets/details-camera-icons/IcEdit';
import usePopUp from '@hooks/usePopUp';

const sdCardStatusMapping: { [key in SdCardStatus]: string } = {
    [SdCardStatus.UNINSERTED]: 'Uninserted',
    [SdCardStatus.INSERTED]: 'Inserted',
    [SdCardStatus.MOUNTED]: 'Mounted',
    [SdCardStatus.INSERT_AND_MOUNT]: 'Inserted and Mounted',
    [SdCardStatus.ERROR]: 'Error',
};

const sdCardDiskStatusMapping: { [key in SdCardDiskStatus]: string } = {
    [SdCardDiskStatus.UNFORMATTED]: 'Unformatted',
    [SdCardDiskStatus.NORMAL]: 'Normal',
    [SdCardDiskStatus.OFFLINE]: 'Offline',
    [SdCardDiskStatus.FORMATTING]: 'Formatting',
    [SdCardDiskStatus.BAD]: 'Bad',
    [SdCardDiskStatus.LOADING]: 'Loading',
    [SdCardDiskStatus.NONE]: 'None',
};

const sdCardFullStatusMapping: { [key in SdCardFullStatus]: string } = {
    [SdCardFullStatus.NO]: 'No',
    [SdCardFullStatus.YES]: 'Yes',
};

const sdEncryptStatusMapping: { [key in SdEncryptStatus]: string } = {
    [SdEncryptStatus.UNENCRYPTED]: 'Unencrypted',
    [SdEncryptStatus.ENCRYPTED_SUCCESS]: 'Encrypted Success',
    [SdEncryptStatus.ENCRYPTED_FAILED]: 'Encrypted Failed',
};

const nasRecycleMapping: { [key in NasRecycle]: string } = {
    [NasRecycle.NO]: 'No',
    [NasRecycle.YES]: 'Yes',
};

const nasMountTypeMapping: { [key in NasMountType]: string } = {
    [NasMountType.NFS]: 'NFS',
    [NasMountType.SMB_CIFS]: 'SMB/CIFS',
};

const nasStatusMapping: { [key in NasStatus]: string } = {
    [NasStatus.UNFORMATTED]: 'Unformatted',
    [NasStatus.NORMAL]: 'Normal',
    [NasStatus.OFFLINE]: 'Offline',
    [NasStatus.FORMATTING]: 'Formatting',
};

const streamTypeMapping: { [key in StreamType]: string } = {
    [StreamType.PrimaryStream]: 'Primary Stream',
    [StreamType.SecondaryStream]: 'Secondary Stream',
    [StreamType.TertiaryStream]: 'Tertiary Stream',
    [StreamType.FourthStream]: 'Fourth Stream',
    [StreamType.FifthStream]: 'Fifth Stream',
};

export const intervalUnitMapping: { [key in IntervalUnit]: string } = {
    [IntervalUnit.SECONDS]: 's',
    [IntervalUnit.MINUTES]: 'm',
    [IntervalUnit.HOURS]: 'h',
    [IntervalUnit.DAYS]: 'd',
};

const storageFilenameTypeMapping: { [key in StorageFilenameType]: string } = {
    [StorageFilenameType.TYPE_0]: 'Type 0',
};

const uploadMethodMapping: { [key in UploadMethod]: string } = {
    [UploadMethod.FTP]: 'FTP',
    [UploadMethod.SMTP]: 'SMTP',
    [UploadMethod.HTTP]: 'HTTP',
};

const tableHeaders: string[] = ['No.', 'Server address', 'Directory', 'Mounting type', 'Total', 'Free'];

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePTZ]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePTZ) => void;
    onSyncPTZConfigs?: () => void;
};

export default function StorageMilesightsCameraPTZ(props: Props) {
    const { camera, filteredCamera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse, onSyncPTZConfigs } = props;

    const configs = camera?.PTZCamera?.PTZCameraConfig;

    const PTZStorageSetting = configs?.StorageSetting;
    const PTZRecordSetting = configs?.RecordSetting;
    const PTZSnapShotSetting = configs?.SnapShotSetting;

    const { updateSnapshotSettings } = useMileSightPTZ({
        filteredCamera,
        cameraPTZ: camera?.PTZCameraId!,
    });

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [buttonSelected, setButtonSelected] = useState<number>(1);
    const [snapshotInfo, setSnapshotInfo] = useState<NotifyItem>({} as NotifyItem);

    const popupEditSnapshotSettings = usePopUp();

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const handleOpenEditPopup = () => {
        popupEditSnapshotSettings.setTrue();
    };

    const handleCloseEditPopup = () => {
        popupEditSnapshotSettings.onClose();
    };

    const handleSaveChanges = async (data: PTZSnapShotSettingFormValues) => {
        try {
            const updateData = {
                TimingSnapshotEnable: data.TimingSnapshotEnable ? 1 : 0,
                IntervalValue: data.IntervalValue,
                IntervalUnit: data.IntervalUnit.Value as IntervalUnit,
            };

            await updateSnapshotSettings(updateData);

            handleCloseEditPopup();
            onSyncPTZConfigs?.();
        } catch (error) {
            console.error('Error updating snapshot settings:', error);
        }
    };

    useEffect(() => {
        const initialData = PTZSnapShotSetting?.NotifyList?.[0];
        if (initialData) {
            setSnapshotInfo(initialData);
            setButtonSelected(initialData.Index + 1);
        }
    }, [PTZSnapShotSetting]);

    const handleButtonClick = (index: number) => {
        setButtonSelected(index);

        const selectedData = PTZSnapShotSetting?.NotifyList?.find((item) => item.Index + 1 === index);
        if (selectedData) {
            setSnapshotInfo((prev) => ({
                ...prev,
                Enable: selectedData.Enable,
                User: selectedData.User,
                Password: selectedData.Password,
                Url: selectedData.Url,
            }));
        }
    };

    const sectionsStorageManagement: SectionHasSubItemProps[] = [
        {
            title: 'SD card',
            data: [
                {
                    label: 'SD card:',
                    value: (
                        <Typography>{`${PTZStorageSetting?.SdcardFreeSize ?? ''}M / ${
                            PTZStorageSetting?.SdcardUseSize ?? ''
                        }M`}</Typography>
                    ),
                },
            ],
        },
        {
            title: 'SD Card Encryption',
            data: [
                {
                    label: 'Encryption status:',
                    value: <Typography>{sdEncryptStatusMapping[PTZStorageSetting?.SdEncryptStatus ?? -1]}</Typography>,
                },
                // {
                //     label: 'Authentication status:',
                //     value: <>-</>,
                // },
                // { label: 'Username:', value: 'admin' },
                // { label: 'Password:', value: 'ms123456' },
            ],
        },
        {
            title: 'NAS',
            tableHeaders: tableHeaders,
            items: PTZStorageSetting?.NasList,
            tableBody: (
                <TableBody>
                    {PTZStorageSetting?.NasList?.map((row, index) => {
                        return (
                            <TableRow key={index}>
                                <CellOfRow value={`${index + 1}`} align="left" />
                                <CellOfRow value={row.NasAddress} align="left" />
                                <CellOfRow value={row.NasFilePath} align="left" />
                                <CellOfRow value={nasMountTypeMapping[row.NasMountType]} align="left" />
                                <CellOfRow value={row.NasTotalSize} align="left" />
                                <CellOfRow value={row.NasFreeSize} align="left" />
                            </TableRow>
                        );
                    })}
                </TableBody>
            ),
        },
    ];

    const sectionsRecordSettings: SectionHasSubItemProps[] = [
        {
            title: 'Storage settings',
            data: [
                {
                    label: 'Enable recycle storage:',
                    value: <Typography>{PTZRecordSetting?.RecycleStorageEnable ? 'Yes' : 'No'}</Typography>,
                },
                {
                    label: 'Stream type:',
                    value: (
                        <Typography>{`${
                            streamTypeMapping[PTZRecordSetting?.StreamType ?? -1]
                        } (Please enable the corresponding stream video)`}</Typography>
                    ),
                },
                {
                    label: 'Pre-record:',
                    value: <Typography>{`${PTZRecordSetting?.PreRecordTime ?? 0}s`}</Typography>,
                },
            ],
        },
        {
            title: 'Schedule settings',
            data: transformScheduleData(PTZRecordSetting?.ScheduleList!, (dayName, schedule) => ({
                label: `${dayName}:`,
                value: schedule,
            })),
        },
    ];

    const sectionsSnapshotSettings: SectionHasSubItemProps[] = [
        {
            title: 'Snapshot settings',
            data: [
                {
                    label: 'Enable timing snapshot:',
                    value: <Typography>{PTZSnapShotSetting?.TimingSnapshotEnable ? 'Yes' : 'No'}</Typography>,
                },
                {
                    label: 'Interval:',
                    value: (
                        <Typography>{`${PTZSnapShotSetting?.IntervalValue ?? ''}${
                            intervalUnitMapping[PTZSnapShotSetting?.IntervalUnit ?? -1]
                        }`}</Typography>
                    ),
                },
                {
                    label: 'Save to storage:',
                    value: <Typography>{PTZSnapShotSetting?.SaveIntoStorage ? 'Yes' : 'No'}</Typography>,
                },
                {
                    label: 'Upload via FTP:',
                    value: <Typography>{PTZSnapShotSetting?.UploadViaFtp ? 'Yes' : 'No'}</Typography>,
                },
                {
                    label: 'Upload via Email:',
                    value: <Typography>{PTZSnapShotSetting?.UploadViaSmtp ? 'Yes' : 'No'}</Typography>,
                },
                {
                    label: 'HTTP post:',
                    value: <Typography>{PTZSnapShotSetting?.UploadViaHttp ? 'Enable' : 'Disable'}</Typography>,
                },
                {
                    label: 'URL:',
                    value: (
                        <Stack direction={'row'} spacing={2}>
                            {PTZSnapShotSetting?.NotifyList?.map((value) => (
                                <Button
                                    key={value.Index}
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleButtonClick(value.Index + 1)}
                                    sx={{
                                        backgroundColor: 'transparent',
                                        border:
                                            buttonSelected === value.Index + 1
                                                ? `1px solid ${color.success}`
                                                : `1px solid ${color.grey200}`,
                                        minWidth: '50px',
                                        '&:hover': {
                                            backgroundColor:
                                                buttonSelected === value.Index + 1 ? '#E5F5ED' : color.grey200,
                                            borderColor:
                                                buttonSelected === value.Index + 1 ? color.success : color.grey200,
                                        },
                                    }}
                                >
                                    <Typography textAlign={'center'} fontSize={12}>
                                        {value.Index + 1}
                                    </Typography>
                                </Button>
                            ))}
                        </Stack>
                    ),
                },
                {
                    label: '',
                    value: <Typography>{snapshotInfo.Url}</Typography>,
                },
                {
                    label: 'Enable:',
                    value: <Typography>{snapshotInfo.Enable ? 'Yes' : 'No'}</Typography>,
                },
                {
                    label: 'User name:',
                    value: <Typography>{snapshotInfo.User}</Typography>,
                },
                {
                    label: 'Password:',
                    value: (
                        <Stack direction={'row'} justifyContent={'space-between'} width={'100%'} alignItems={'center'}>
                            <Typography>
                                {showPassword ? snapshotInfo.Password : snapshotInfo.Password?.replace(/./g, '*')}
                            </Typography>

                            {showPassword ? (
                                <BiHide
                                    fontSize={18}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPassword(false)}
                                />
                            ) : (
                                <BiShow
                                    fontSize={18}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPassword(true)}
                                />
                            )}
                        </Stack>
                    ),
                },
            ],
        },
        {
            title: 'Schedule settings',
            data: transformScheduleData(PTZSnapShotSetting?.ScheduleList!, (dayName, schedule) => ({
                label: `${dayName}:`,
                value: schedule,
            })),
        },
    ];

    return (
        <Stack id="storageManagement" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            <SectionHasSubItemHeader id="" title="Storage" onEdit={handleEdit} />

            <BaseSectionCollapse
                title="Storage management"
                isOpen={isOpenCollapse.storageManagement}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.StorageManagement);
                }}
                sections={sectionsStorageManagement}
            />

            <BaseSectionCollapse
                title="Record settings"
                id="recordSettings"
                isOpen={isOpenCollapse.recordSettings}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.RecordSettings);
                }}
                sections={sectionsRecordSettings}
            />

            <BaseSectionCollapse
                title="Snapshot settings"
                id="snapshotSettings"
                isOpen={isOpenCollapse.snapshotSettings}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.SnapshotSettings);
                }}
                sections={sectionsSnapshotSettings}
                action={
                    <IconButton onClick={handleOpenEditPopup} size="small">
                        <IcEdit style={{ width: '14px', height: '14px' }} />
                    </IconButton>
                }
            />

            <PopUpEditSnapshotSettings
                {...popupEditSnapshotSettings}
                handleUpdateSnapshotSettings={handleSaveChanges}
                ptzSnapShotSetting={PTZSnapShotSetting}
            />
        </Stack>
    );
}
